import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface Props {
  onContactClick: () => void;
}

const navLinks = [
  { label: 'Home', path: '/', icon: 'pi-home' },
  { label: 'Leistungen', path: '/leistungen', icon: 'pi-list-check' },
  { label: 'Wissen', path: '/wissen', icon: 'pi-book' },
  { label: 'Über mich', path: '/ueber-mich', icon: 'pi-user' },
];

const Header = ({ onContactClick }: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled
            ? 'border-ak-sky/25 bg-ak-navy/[0.97] shadow-[0_4px_24px_rgba(0,0,0,0.3)] backdrop-blur-xl'
            : 'border-ak-sky/15 bg-ak-navy'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          {/* Logo + Brand */}
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <img
              src="/assets/logo_square_96.png"
              alt="AK TechSolutions Logo"
              width={36}
              height={36}
              className="h-9 w-9 object-contain"
            />
            <div>
              <div className="text-sm font-bold leading-tight text-white">Alexander Krein</div>
              <div className="text-[11px] font-medium tracking-wide text-ak-sky">TechSolutions</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-medium no-underline transition-colors ${
                  isActive(l.path)
                    ? 'border-ak-sky/25 bg-ak-sky/[0.12] text-ak-sky'
                    : 'border-transparent text-ak-muted hover:bg-ak-sky/[0.08] hover:text-ak-sky'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Desktop Kontakt-Button */}
            <button
              onClick={onContactClick}
              className="btn-primary btn-sm hidden text-sm md:inline-flex"
            >
              <i className="pi pi-envelope text-sm"></i>
              Kontakt
            </button>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center rounded-lg border border-ak-sky/30 bg-transparent px-2.5 py-1.5 text-lg text-ak-sky md:hidden"
              aria-label="Menü öffnen"
            >
              <i className="pi pi-bars"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="flex h-full w-4/5 max-w-[300px] flex-col gap-1 border-r border-ak-sky/15 bg-ak-navy p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img
                  src="/assets/logo_square_96.png"
                  alt="AK TechSolutions Logo"
                  width={36}
                  height={36}
                  className="h-9 w-9 object-contain"
                />
                <div>
                  <div className="text-sm font-bold leading-tight text-white">Alexander Krein</div>
                  <div className="text-[11px] font-medium tracking-wide text-ak-sky">TechSolutions</div>
                </div>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Menü schließen"
                className="cursor-pointer border-0 bg-transparent text-xl text-ak-sky"
              >
                <i className="pi pi-times"></i>
              </button>
            </div>

            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 rounded-lg border-b border-white/5 px-3 py-3.5 text-[15px] no-underline ${
                  isActive(l.path) ? 'bg-ak-sky/[0.07] font-bold text-ak-sky' : 'font-normal text-ak-muted'
                }`}
              >
                <i className={`pi ${l.icon} w-[18px] text-center ${isActive(l.path) ? 'text-ak-sky' : 'text-ak-dim'}`}></i>
                {l.label}
              </Link>
            ))}

            <div className="mt-4">
              <button
                onClick={() => { onContactClick(); setMenuOpen(false); }}
                className="btn-primary w-full justify-center py-3 text-sm"
              >
                <i className="pi pi-envelope"></i>
                Kontakt aufnehmen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
